import { useState, useEffect, useCallback } from 'react';
import { api } from '@expat-ui/shared';
import { getFreshAccessToken } from '../utils/authUtils';
import {
    UserProfile,
    AssetAccount,
    HouseholdResponse,
    DynamicCashFlow,
    TaxAnalysisResult,
    Goal,
} from '../types/api.types';

function useApiCall<T>(fetcher: (token: string) => Promise<T>) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const execute = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            const result = await fetcher(token);
            setData(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Request failed');
        } finally {
            setLoading(false);
        }
    }, [fetcher]);

    useEffect(() => {
        execute();
    }, [execute]);

    return { data, loading, error, refetch: execute };
}

export function useProfile() {
    const fetcher = useCallback((token: string) => api.get<UserProfile>('/profile', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const updateProfile = async (profile: Partial<UserProfile>) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.put<UserProfile>('/profile', profile, token);
        refetch();
        return result;
    };

    return { profile: data, loading, error, refetch, updateProfile };
}

export function useAssets() {
    const fetcher = useCallback((token: string) => api.get<AssetAccount[]>('/assets', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const addAsset = async (asset: Omit<AssetAccount, 'accountId' | 'memberSub' | 'updatedAt' | 'pk' | 'sk'>) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.post<AssetAccount>('/assets', asset, token);
        refetch();
        return result;
    };

    const updateAsset = async (
        accountId: string,
        patch: Partial<Pick<AssetAccount, 'balance' | 'expectedReturn' | 'ownership' | 'linkedGoalId'>>,
    ) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.put<AssetAccount>(`/assets/${accountId}`, patch, token);
        refetch();
        return result;
    };

    const deleteAsset = async (accountId: string) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        await api.delete(`/assets/${accountId}`, token);
        refetch();
    };

    return { assets: data || [], loading, error, refetch, addAsset, updateAsset, deleteAsset };
}

export function useHousehold() {
    const fetcher = useCallback((token: string) => api.get<HouseholdResponse>('/household', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const createHousehold = async (name: string) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.post<HouseholdResponse>('/household', { name }, token);
        refetch();
        return result;
    };

    return { household: data, loading, error, refetch, createHousehold };
}

export function useCashflows() {
    const fetcher = useCallback((token: string) => api.get<DynamicCashFlow[]>('/cashflows', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);

    const addCashflow = async (cashflow: Omit<DynamicCashFlow, 'cashFlowId' | 'memberSub' | 'updatedAt'>) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        const result = await api.post<DynamicCashFlow>('/cashflows', cashflow, token);
        refetch();
        return result;
    };

    const deleteCashflow = async (cashFlowId: string) => {
        const token = await getFreshAccessToken();
        if (!token) throw new Error('Not authenticated');
        await api.delete(`/cashflows/${cashFlowId}`, token);
        refetch();
    };

    return { cashflows: data || [], loading, error, refetch, addCashflow, deleteCashflow };
}

export function useGoals() {
    const fetcher = useCallback((token: string) => api.get<Goal[]>('/goals', token), []);
    const { data, loading, error, refetch } = useApiCall(fetcher);
    return { goals: data || [], loading, error, refetch };
}

export function useTaxAnalysis() {
    const fetcher = useCallback((token: string) => api.get<TaxAnalysisResult>('/tax', token), []);
    return useApiCall<TaxAnalysisResult>(fetcher);
}
